import React from 'react';
import { ReportData } from '../../types/report.types';
import HtmlEditor from '../HtmlEditor';

interface ScopeEditorProps {
  reportData: ReportData;
  onHtmlChange: (field: keyof ReportData, value: string) => void;
  onTargetDetailsChange: (index: number, field: string, value: any) => void;
  onAddTarget: () => void;
  onRemoveTarget: (index: number) => void;
}

/** Detect whether a target is a new V2 structured target (from asset_targets) */
function isV2Target(target: any): boolean {
  return !!(target.target_value !== undefined || target.target_type !== undefined);
}

const ScopeEditor: React.FC<ScopeEditorProps> = ({
  reportData,
  onHtmlChange,
  onTargetDetailsChange,
  onAddTarget,
  onRemoveTarget
}) => {
  return (
    <div className="h-full overflow-y-auto space-y-6">
      {/* Scope Content Section */}
      <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full" />
          <div>
            <h3 className="text-base font-bold text-blue-900 tracking-tight">Scope Content</h3>
            <p className="text-xs text-blue-700/80 font-medium mt-0.5">Define the scope of your assessment</p>
          </div>
        </div>
        <div className="pt-2">
          <div className="flex gap-1 mb-2">
            <button
              onClick={() => {
                const editor = document.querySelector('[data-field="scope"]');
                if (editor) {
                  const selection = window.getSelection();
                  const range = selection?.getRangeAt(0);
                  const strong = document.createElement('strong');
                  range?.surroundContents(strong);
                }
              }}
              className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors duration-200 flex items-center gap-1"
            >
              <span className="font-bold">B</span>
              <span>Bold</span>
            </button>
            <button
              onClick={() => {
                const editor = document.querySelector('[data-field="scope"]');
                if (editor) {
                  const selection = window.getSelection();
                  const range = selection?.getRangeAt(0);
                  const em = document.createElement('em');
                  range?.surroundContents(em);
                }
              }}
              className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors duration-200 flex items-center gap-1"
            >
              <span className="italic">I</span>
              <span>Italic</span>
            </button>
            <button
              onClick={() => {
                const editor = document.querySelector('[data-field="scope"]');
                if (editor) {
                  const p = document.createElement('p');
                  p.innerHTML = '<br>';
                  editor.appendChild(p);
                }
              }}
              className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors duration-200 flex items-center gap-1"
            >
              <span>¶</span>
              <span>New Line</span>
            </button>
          </div>
          <HtmlEditor
            field="scope"
            title=""
            reportData={reportData}
            onHtmlChange={onHtmlChange}
          />
        </div>
      </div>

      {/* Target Details Section */}
      <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6 mb-6">
        <div className="flex items-center gap-3 mb-4 justify-between">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full" />
            <div>
              <h3 className="text-base font-bold text-blue-900 tracking-tight">Target Details</h3>
              <p className="text-xs text-blue-700/80 font-medium mt-0.5">Manage assessment targets and their URLs</p>
            </div>
          </div>
          <button
            onClick={onAddTarget}
            className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-colors duration-200 flex items-center gap-1.5 shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Add Target
          </button>
        </div>
        <div className="pt-2">
          {/* Info banner for V2 targets */}
          {reportData.target_details?.some(isV2Target) && (
            <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
              <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xs text-blue-700">
                These targets are imported from the program scope. You can modify them below for this report specifically.
              </p>
            </div>
          )}
          {reportData.target_details?.length === 0 ? (
            <div className="text-center py-8 bg-blue-50 rounded-lg border border-dashed border-blue-200">
              <svg className="w-12 h-12 mx-auto text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <p className="mt-2 text-sm text-blue-700">No targets added yet</p>
              <button
                onClick={onAddTarget}
                className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Add your first target
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {reportData.target_details?.map((target, index) => (
                <div key={index} className="group relative bg-blue-50 rounded-lg p-3 border border-blue-100 hover:border-blue-300 transition-colors duration-200">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-blue-900 mb-1">Target Type</label>
                        <input
                          type="text"
                          value={target.target_type || target.type || ''}
                          onChange={(e) => {
                            if (isV2Target(target)) {
                              onTargetDetailsChange(index, 'target_type', e.target.value);
                            } else {
                              onTargetDetailsChange(index, 'type', e.target.value);
                            }
                          }}
                          className="w-full px-2.5 py-1.5 text-sm border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                          placeholder="e.g., Web Application, API, Mobile App"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-blue-900 mb-1">Target URL / Asset</label>
                        <input
                          type="text"
                          value={target.target_value || target.url || ''}
                          onChange={(e) => {
                            if (isV2Target(target)) {
                              onTargetDetailsChange(index, 'target_value', e.target.value);
                            } else {
                              onTargetDetailsChange(index, 'url', e.target.value);
                            }
                          }}
                          className="w-full px-2.5 py-1.5 text-sm border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                          placeholder="e.g., https://example.com"
                        />
                      </div>
                      {reportData.template === 'template-2' && (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs font-medium text-blue-900 mb-1">Description (for Template 2)</label>
                              <input
                                type="text"
                                value={target.description || ''}
                                onChange={(e) => onTargetDetailsChange(index, 'description', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                                placeholder="e.g., Customer Portal Website"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-blue-900 mb-1">Tech Stack (for Template 2)</label>
                              <input
                                type="text"
                                value={target.tech_stack || target.technology || ''}
                                onChange={(e) => onTargetDetailsChange(index, 'tech_stack', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-blue-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                                placeholder="e.g., React, Node.js"
                              />
                            </div>
                          </div>
                          <div className="border-t border-blue-100/50 pt-2 mt-2">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[10px] font-bold text-blue-800 uppercase tracking-wider block">User Role Configurations (for Scope & Attestation)</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const currentRoles = target.roles && Array.isArray(target.roles) ? target.roles : (
                                    (target.role || target.privilege_level || target.credentials) ? [
                                      { role: target.role || '', privilege_level: target.privilege_level || '', credentials: target.credentials || '' }
                                    ] : []
                                  );
                                  onTargetDetailsChange(index, 'roles', [...currentRoles, { role: '', privilege_level: '', credentials: '' }]);
                                }}
                                className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded hover:bg-blue-200 transition-colors"
                              >
                                + Add Role
                              </button>
                            </div>
                            {(() => {
                              const currentRoles = target.roles && Array.isArray(target.roles) ? target.roles : (
                                (target.role || target.privilege_level || target.credentials) ? [
                                  { role: target.role || '', privilege_level: target.privilege_level || '', credentials: target.credentials || '' }
                                ] : []
                              );
                              return currentRoles.length === 0 ? (
                                <p className="text-[10px] text-gray-500 italic">No roles added. Click "+ Add Role" to specify credentials.</p>
                              ) : (
                                <div className="space-y-2">
                                  {currentRoles.map((r: any, rIdx: number) => (
                                    <div key={rIdx} className="flex gap-2 items-end bg-white p-2 rounded-md border border-blue-100/70">
                                      <div className="flex-1">
                                        <label className="block text-[9px] font-medium text-gray-500 mb-0.5">Role Name</label>
                                        <input
                                          type="text"
                                          value={r.role || ''}
                                          onChange={(e) => {
                                            const updatedRoles = [...currentRoles];
                                            updatedRoles[rIdx] = { ...updatedRoles[rIdx], role: e.target.value };
                                            onTargetDetailsChange(index, 'roles', updatedRoles);
                                          }}
                                          className="w-full px-2 py-1 text-xs border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                                          placeholder="e.g., Standard User"
                                        />
                                      </div>
                                      <div className="flex-1">
                                        <label className="block text-[9px] font-medium text-gray-500 mb-0.5">Privilege Level</label>
                                        <input
                                          type="text"
                                          value={r.privilege_level || ''}
                                          onChange={(e) => {
                                            const updatedRoles = [...currentRoles];
                                            updatedRoles[rIdx] = { ...updatedRoles[rIdx], privilege_level: e.target.value };
                                            onTargetDetailsChange(index, 'roles', updatedRoles);
                                          }}
                                          className="w-full px-2 py-1 text-xs border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                                          placeholder="e.g., Low / High"
                                        />
                                      </div>
                                      <div className="flex-1">
                                        <label className="block text-[9px] font-medium text-gray-500 mb-0.5">Credentials</label>
                                        <input
                                          type="text"
                                          value={r.credentials || ''}
                                          onChange={(e) => {
                                            const updatedRoles = [...currentRoles];
                                            updatedRoles[rIdx] = { ...updatedRoles[rIdx], credentials: e.target.value };
                                            onTargetDetailsChange(index, 'roles', updatedRoles);
                                          }}
                                          className="w-full px-2 py-1 text-xs border border-blue-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                                          placeholder="e.g., Provided"
                                        />
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const updatedRoles = currentRoles.filter((_: any, idx: number) => idx !== rIdx);
                                          onTargetDetailsChange(index, 'roles', updatedRoles);
                                        }}
                                        className="text-red-500 hover:text-red-700 p-1 flex-shrink-0 transition-colors"
                                        title="Remove role"
                                      >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                        </>
                      )}
                      {target.endpoints && Array.isArray(target.endpoints) && target.endpoints.length > 0 && (
                        <div>
                          <label className="block text-xs font-medium text-blue-900 mb-1">
                            Endpoints ({target.endpoints.length})
                          </label>
                          <div className="flex flex-wrap gap-1">
                            {target.endpoints.slice(0, 5).map((ep: any, epIdx: number) => (
                              <span key={epIdx} className="text-xs px-1.5 py-0.5 bg-white border border-slate-200 text-slate-600 rounded font-mono truncate max-w-[180px]">
                                {ep.value || ep}
                              </span>
                            ))}
                            {target.endpoints.length > 5 && (
                              <span className="text-xs px-1.5 py-0.5 text-slate-400">+{target.endpoints.length - 5} more</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => onRemoveTarget(index)}
                      className="opacity-0 group-hover:opacity-100 text-blue-400 hover:text-red-600 transition-all duration-200 flex-shrink-0"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Additional Notes Section (Under the table) */}
      <div className="bg-white rounded-xl shadow-lg border border-slate-100 p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-blue-700 rounded-full" />
          <div>
            <h3 className="text-base font-bold text-blue-900 tracking-tight">Additional Notes</h3>
            <p className="text-xs text-blue-700/80 font-medium mt-0.5">Add any extra context you want to appear under the Scope table</p>
          </div>
        </div>
        <div className="pt-2">
          <HtmlEditor
            field="scope_notes"
            title=""
            reportData={reportData}
            onHtmlChange={onHtmlChange}
            useJodit={true}
          />
        </div>
      </div>

    </div>
  );
};

export default ScopeEditor;